import { Fragment, useRef, useState } from "react";

import { mibs, type MibModule, type MibUploadResult } from "../lib/api.js";
import { MIB_STATUS_COLOR, mibStatusLabel } from "../lib/mib-status.js";
import { useLiveData } from "../lib/use-live-data.js";

/**
 * The organization's MIB library.
 *
 * Upload a vendor's MIBs -- a whole zip is the normal case -- and their
 * objects become choosable on any device's custom metrics, with no OIDs
 * typed by hand. Every upload re-parses the whole library, because MIBs
 * import each other: one uploaded today can fix one uploaded last week.
 */

/** Matches the server's limit; checked here so a too-big upload fails before it is sent. */
const MAX_TOTAL_BYTES = 30 * 1024 * 1024;

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result);
      resolve(url.slice(url.indexOf(",") + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error(`Could not read ${file.name}`));
    reader.readAsDataURL(file);
  });
}

export function MibsPage() {
  const { data, error, reload } = useLiveData(() => mibs.list(), 300_000);
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<MibUploadResult | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const canManage = data?.canManage ?? false;
  const modules = (data?.modules ?? []).filter((m) =>
    filter === "" ? true : `${m.name} ${m.filename}`.toLowerCase().includes(filter.toLowerCase()),
  );

  const upload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setActionError(null);
    setResult(null);
    const list = [...files];
    const total = list.reduce((n, f) => n + f.size, 0);
    if (total > MAX_TOTAL_BYTES) {
      setActionError(`That is ${(total / 1024 / 1024).toFixed(1)} MB; the limit is 30 MB per upload.`);
      return;
    }
    setBusy(`Reading ${list.length} file${list.length === 1 ? "" : "s"}…`);
    try {
      const encoded = await Promise.all(list.map(async (f) => ({ name: f.name, data_base64: await readAsBase64(f) })));
      setBusy("Parsing… this can take a few seconds.");
      setResult(await mibs.upload(encoded));
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setBusy(null);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const remove = async (module: MibModule) => {
    if (!confirm(`Remove ${module.name}? MIBs that import it may break. Existing metrics keep collecting.`)) return;
    setActionError(null);
    setBusy(`Removing ${module.name} and re-parsing…`);
    try {
      setResult(await mibs.remove(module.id));
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not remove");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <h1 style={{ fontSize: 15, margin: "0 0 6px", letterSpacing: "0.14em" }}>&gt; MIB LIBRARY</h1>
      <p style={help}>
        Upload a manufacturer's MIB files (or a .zip) to pick their readings by name under CUSTOM METRICS on any device.
      </p>

      {canManage && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
          <input
            ref={fileInput}
            type="file"
            multiple
            accept=".mib,.my,.txt,.mi2,.smi,.zip"
            onChange={(e) => void upload(e.target.files)}
            style={{ display: "none" }}
          />
          <button disabled={busy !== null} onClick={() => fileInput.current?.click()} style={smallButton}>
            [ + UPLOAD MIBS ]
          </button>
          <span style={{ ...help, margin: 0 }}>.mib, .txt, .my or a .zip of them</span>
        </div>
      )}

      {busy && <p style={{ fontSize: 12, color: "var(--text-secondary)" }}>{`>> ${busy}`}</p>}
      {actionError && <p style={{ color: "var(--status-critical)", fontSize: 12 }}>{`!! ${actionError}`}</p>}
      {error && <p style={{ color: "var(--status-critical)", fontSize: 12 }}>{`!! ${error}`}</p>}
      {result && <UploadSummary result={result} />}

      {data && data.modules.length === 0 && <p style={{ color: "var(--text-muted)", fontSize: 13 }}>-- NO MIBS YET --</p>}

      {data && data.modules.length > 0 && (
        <>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={`filter ${data.modules.length} modules`}
            style={{ ...input, marginBottom: 10, width: 260 }}
          />
          <div style={{ border: "1px solid var(--border)", background: "var(--surface-1)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border)" }}>
                  {["STATUS", "MODULE", "FILE", "OBJECTS", "GRAPHABLE", ...(canManage ? [""] : [])].map((h, i) => (
                    <th key={h || `a${i}`} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {modules.map((m) => (
                  <Fragment key={m.id}>
                    <tr style={{ borderBottom: "1px solid var(--gridline)" }}>
                      <td style={td}>
                        {m.problems.length > 0 ? (
                          <button
                            onClick={() => setOpen(open === m.id ? null : m.id)}
                            title="Show what the parser said"
                            style={{ ...statusStyle(m), background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}
                          >
                            {mibStatusLabel(m.status)}
                          </button>
                        ) : (
                          <span style={{ ...statusStyle(m), fontSize: 12 }}>{mibStatusLabel(m.status)}</span>
                        )}
                      </td>
                      <td style={td}>{m.name}</td>
                      <td style={{ ...td, color: "var(--text-secondary)", fontSize: 12 }}>{m.filename}</td>
                      <td style={{ ...td, color: "var(--text-secondary)" }}>{m.object_count}</td>
                      <td style={{ ...td, color: "var(--text-secondary)" }}>{m.graphable}</td>
                      {canManage && (
                        <td style={{ ...td, textAlign: "right" }}>
                          <button disabled={busy !== null} onClick={() => void remove(m)} style={smallButton}>[ X ]</button>
                        </td>
                      )}
                    </tr>
                    {open === m.id && (
                      <tr style={{ borderBottom: "1px solid var(--gridline)" }}>
                        <td colSpan={canManage ? 6 : 5} style={{ ...td, paddingTop: 0 }}>
                          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.6 }}>
                            {m.problems.map((p, i) => <li key={i}>{p}</li>)}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function statusStyle(m: MibModule): React.CSSProperties {
  return { color: MIB_STATUS_COLOR[m.status], whiteSpace: "pre" };
}

function UploadSummary({ result }: { result: MibUploadResult }) {
  return (
    <div style={{ border: "1px solid var(--border)", padding: "10px 14px", marginBottom: 14, fontSize: 12, lineHeight: 1.7 }}>
      <div>
        {result.uploaded !== undefined && `${result.uploaded} file${result.uploaded === 1 ? "" : "s"} uploaded. `}
        Library: {result.modules} modules — {result.ok} ok, {result.warnings} with warnings, {result.broken} broken.{" "}
        {result.graphable} of {result.objects} objects can be graphed.
      </div>
      {result.missing_imports.length > 0 && (
        <div style={{ color: "var(--status-warning)" }}>
          Missing: {result.missing_imports.join(", ")}. Upload {result.missing_imports.length === 1 ? "it" : "them"} to fix the WARN and BROKEN modules.
        </div>
      )}
      {result.skipped && result.skipped.length > 0 && (
        <div style={{ color: "var(--text-muted)" }}>
          Skipped: {result.skipped.map((s) => `${s.name} (${s.reason})`).join(", ")}
        </div>
      )}
    </div>
  );
}

const help: React.CSSProperties = { color: "var(--text-muted)", fontSize: 11, margin: "0 0 14px", lineHeight: 1.6 };
const th: React.CSSProperties = { padding: "10px 14px", color: "var(--text-muted)", fontWeight: 500 };
const td: React.CSSProperties = { padding: "8px 14px" };
const input: React.CSSProperties = {
  padding: "7px 9px", fontSize: 13, border: "1px solid var(--border)",
  background: "var(--surface-raised)", color: "var(--text-primary)",
};
const smallButton: React.CSSProperties = {
  background: "none", border: "1px solid var(--border)", padding: "5px 9px",
  fontSize: 11, color: "var(--text-secondary)", cursor: "pointer", letterSpacing: "0.08em", whiteSpace: "nowrap",
};
